import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  AddCircleOutline,
  Cancel as CancelIcon,
  CheckCircle,
  CloudUpload,
  Delete as DeleteIcon,
  InfoOutlined,
  LiveHelp,
  RestartAlt,
} from '@mui/icons-material';
import { addManualCartItem, deleteUploadedImage, uploadManualCartImage } from '../../services/manualCart';
import { useTheme } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import useMediaQuery from '@mui/material/useMediaQuery';

const createRandomId = () => Math.floor(100000 + Math.random() * 900000).toString();
const createImageId = () => `${Date.now()}-${Math.random()}`;
const maxFileSize = 10 * 1024 * 1024;
const acceptedFileTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const defaultProfitPercentage = 5;
const minProfitPercentage = 0;
const maxProfitPercentage = 100;

const parseBoundedProfitPercentage = (value) => {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) {
    return defaultProfitPercentage;
  }
  return Math.min(maxProfitPercentage, Math.max(minProfitPercentage, parsedValue));
};

const applyProfitPercentage = (value, percentage) => {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) {
    return 0;
  }
  return Number((parsedValue * (1 + percentage / 100)).toFixed(2));
};

const createEmptySpec = () => ({
  id: `${Date.now()}-${Math.random()}`,
  name: '',
  value: '',
  imageUrl: '',
  price: '',
  quantity: '',
  confirmed: false,
  skuId: null,
  variants: [],
});

const createEmptyVariant = () => ({
  id: `${Date.now()}-${Math.random()}`,
  name: '',
  value: '',
  imageUrl: '',
  price: '',
  quantity: '',
  confirmed: false,
  skuId: null,
});

const ManualItemAddPage = () => {
  const { t } = useTranslation(['manualItemAdd', 'common']);
  const fileInputRef = useRef(null);
  const [formState, setFormState] = useState({
    title: '',
    userIdentifier: '',
    price: '',
    profitPercentage: defaultProfitPercentage.toString(),
    postFee: '',
    description: '',
    detailUrl: '',
    isProductRequestItem: false,
    productRequestNumber: '',
    productRequestItemId: '',
  });
  const [imageState, setImageState] = useState({
    files: [],
    uploading: false,
    dragActive: false,
  });
  const [errors, setErrors] = useState({});
  const [specs, setSpecs] = useState([]);
  const [skuMap, setSkuMap] = useState({});
  const [previewPayload, setPreviewPayload] = useState(null);
  const [manualResponse, setManualResponse] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: 'success',
    message: '',
  });
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => () => {
    imageState.files.forEach((fileEntry) => {
      URL.revokeObjectURL(fileEntry.preview);
    });
  }, [imageState.files]);

  const resetForm = useCallback(() => {
    imageState.files.forEach((fileEntry) => {
      URL.revokeObjectURL(fileEntry.preview);
    });
    setFormState({
      title: '',
      userIdentifier: '',
      price: '',
      profitPercentage: defaultProfitPercentage.toString(),
      postFee: '',
      description: '',
      detailUrl: '',
      isProductRequestItem: false,
      productRequestNumber: '',
      productRequestItemId: '',
    });
    setImageState({ files: [], uploading: false, dragActive: false });
    setErrors({});
    setSpecs([]);
    setSkuMap({});
    setPreviewPayload(null);
    setManualResponse(null);
    setSnackbar({
      open: true,
      severity: 'info',
      message: t('manualItemAdd:formResetSuccess'),
    });
  }, [imageState.files, t]);

  const handleInputChange = (field) => (event) => {
    const value = field === 'isProductRequestItem' ? event.target.checked : event.target.value;
    setFormState((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'isProductRequestItem' && !event.target.checked ? { productRequestNumber: '', productRequestItemId: '' } : {}),
    }));
    setErrors((prev) => ({
      ...prev,
      [field]: '',
      ...(field === 'isProductRequestItem' || field === 'productRequestNumber' || field === 'productRequestItemId'
        ? { productRequestNumber: '', productRequestItemId: '' }
        : {}),
    }));
  };

  const handleUploadClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleDrag = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === 'dragenter' || event.type === 'dragover') {
      setImageState((prev) => ({ ...prev, dragActive: true }));
    } else if (event.type === 'dragleave') {
      setImageState((prev) => ({ ...prev, dragActive: false }));
    }
  }, []);

  const handleFileSelect = useCallback(async (files) => {
    const uploadedFiles = [];
    const selectedFiles = Array.from(files || []).filter((file) => {
      if (!acceptedFileTypes.includes(file.type)) {
        const message = t('manualItemAdd:validation.invalidFileType', { fileName: file.name });
        setErrors((prev) => ({ ...prev, image: message }));
        setSnackbar({ open: true, severity: 'error', message });
        return false;
      }
      if (file.size > maxFileSize) {
        const message = t('manualItemAdd:validation.fileTooLarge', {
          fileName: file.name,
          maxSize: Math.round(maxFileSize / (1024 * 1024)),
        });
        setErrors((prev) => ({ ...prev, image: message }));
        setSnackbar({ open: true, severity: 'error', message });
        return false;
      }
      return true;
    });

    if (!selectedFiles.length) {
      return;
    }

    const newFiles = selectedFiles.map((file) => ({
      id: createImageId(),
      file,
      name: file.name,
      size: file.size,
      preview: URL.createObjectURL(file),
      fileId: null,
      uploadedUrl: '',
      deleting: false,
    }));

    setErrors((prev) => ({ ...prev, image: '' }));
    setImageState((prev) => ({
      ...prev,
      files: [...prev.files, ...newFiles],
      uploading: true,
      dragActive: false,
    }));

    try {
      for (const fileEntry of newFiles) {
        const response = await uploadManualCartImage(fileEntry.file);
        const uploadedUrl = response?.file_url || response?.url || response?.uri;
        if (!uploadedUrl) {
          throw new Error(t('manualItemAdd:messages.uploadMissingUrl'));
        }
        uploadedFiles.push({
          ...fileEntry,
          fileId: response?.file_id || null,
          uploadedUrl,
        });
      }

      const uploadedMap = new Map(uploadedFiles.map((fileEntry) => [fileEntry.id, fileEntry]));
      setImageState((prev) => ({
        ...prev,
        files: prev.files.map((fileEntry) =>
          uploadedMap.has(fileEntry.id)
            ? { ...fileEntry, ...uploadedMap.get(fileEntry.id) }
            : fileEntry
        ),
        uploading: false,
      }));
      setSnackbar({
        open: true,
        severity: 'success',
        message: t('manualItemAdd:messages.imagesUploadedSuccess', { count: uploadedFiles.length }),
      });
    } catch (error) {
      await Promise.all(
        uploadedFiles
          .filter((fileEntry) => fileEntry.fileId)
          .map((fileEntry) => deleteUploadedImage(fileEntry.fileId).catch(() => null))
      );
      const failedFileIds = new Set(newFiles.map((fileEntry) => fileEntry.id));
      newFiles.forEach((fileEntry) => {
        URL.revokeObjectURL(fileEntry.preview);
      });
      setImageState((prev) => ({
        ...prev,
        files: prev.files.filter((fileEntry) => !failedFileIds.has(fileEntry.id)),
        uploading: false,
      }));
      const message =
        error?.response?.data?.error ||
        error?.message ||
        t('manualItemAdd:messages.failedToUploadImage');
      setErrors((prev) => ({
        ...prev,
        image: message,
      }));
      setSnackbar({
        open: true,
        severity: 'error',
        message,
      });
    }
  }, [t]);

  const handleRemoveImage = useCallback(async (imageId) => {
    const target = imageState.files.find((fileEntry) => fileEntry.id === imageId);
    if (!target) {
      return;
    }

    setImageState((prev) => ({
      ...prev,
      files: prev.files.map((fileEntry) =>
        fileEntry.id === imageId
          ? { ...fileEntry, deleting: true }
          : fileEntry
      ),
    }));

    try {
      if (target.fileId) {
        await deleteUploadedImage(target.fileId);
      }
      if (target.preview) {
        URL.revokeObjectURL(target.preview);
      }

      setImageState((prev) => ({
        ...prev,
        files: prev.files.filter((fileEntry) => fileEntry.id !== imageId),
      }));
      setSnackbar({
        open: true,
        severity: 'success',
        message: t('manualItemAdd:messages.imageDeleted'),
      });
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        t('manualItemAdd:messages.imageDeleteFailed');
      setImageState((prev) => ({
        ...prev,
        files: prev.files.map((fileEntry) =>
          fileEntry.id === imageId
            ? { ...fileEntry, deleting: false }
            : fileEntry
        ),
      }));
      setErrors((prev) => ({ ...prev, image: message }));
      setSnackbar({
        open: true,
        severity: 'error',
        message,
      });
    }
  }, [imageState.files, t]);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setImageState((prev) => ({ ...prev, dragActive: false }));
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      handleFileSelect(event.dataTransfer.files);
      event.dataTransfer.clearData();
    }
  }, [handleFileSelect]);

  const handleImageChange = useCallback((event) => {
    if (event.target.files && event.target.files.length > 0) {
      handleFileSelect(event.target.files);
    }
    event.target.value = '';
  }, [handleFileSelect]);

  const formatFileSize = useCallback((bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }, []);

  const removeSkuEntry = useCallback((skuId) => {
    if (!skuId) {
      return;
    }
    setSkuMap((prev) => {
      if (!prev[skuId]) {
        return prev;
      }
      const updated = { ...prev };
      delete updated[skuId];
      return updated;
    });
  }, []);

  const handleAddSpec = () => {
    setSpecs((prev) => [...prev, createEmptySpec()]);
  };

  const handleRemoveSpec = (specId) => {
    setSpecs((prev) => {
      const target = prev.find((spec) => spec.id === specId);
      if (target) {
        if (target.skuId) {
          removeSkuEntry(target.skuId);
        }
        target.variants.forEach((variant) => {
          if (variant.skuId) {
            removeSkuEntry(variant.skuId);
          }
        });
      }
      return prev.filter((spec) => spec.id !== specId);
    });
  };

  const updateSpecField = (specId, field, value) => {
    setSpecs((prev) =>
      prev.map((spec) => {
        if (spec.id !== specId) {
          return spec;
        }
        if (field !== 'variants' && spec.skuId) {
          removeSkuEntry(spec.skuId);
        }
        return {
          ...spec,
          [field]: value,
          confirmed: false,
          skuId: field === 'variants' ? spec.skuId : null,
        };
      })
    );
  };

  const updateVariantField = (specId, variantId, field, value) => {
    setSpecs((prev) =>
      prev.map((spec) => {
        if (spec.id !== specId) {
          return spec;
        }
        const variants = spec.variants.map((variant) => {
          if (variant.id !== variantId) {
            return variant;
          }
          if (variant.skuId) {
            removeSkuEntry(variant.skuId);
          }
          return {
            ...variant,
            [field]: value,
            confirmed: false,
            skuId: null,
          };
        });
        return { ...spec, variants };
      })
    );
  };

  const handleAddVariant = (specId) => {
    setSpecs((prev) =>
      prev.map((spec) => {
        if (spec.id !== specId) {
          return spec;
        }
        return {
          ...spec,
          variants: [...spec.variants, createEmptyVariant()],
        };
      })
    );
  };

  const handleRemoveVariant = (specId, variantId) => {
    setSpecs((prev) =>
      prev.map((spec) => {
        if (spec.id !== specId) {
          return spec;
        }
        const targetVariant = spec.variants.find((variant) => variant.id === variantId);
        if (targetVariant?.skuId) {
          removeSkuEntry(targetVariant.skuId);
        }
        return {
          ...spec,
          variants: spec.variants.filter((variant) => variant.id !== variantId),
        };
      })
    );
  };

  const buildSkuPayload = ({
    specIndex,
    variantIndex,
    specName,
    specValue,
    variantName,
    variantValue,
    imageUrl,
    price,
    quantity,
  }) => {
    const parsedPrice = Number(price);
    const parsedQty = Number(quantity);
    const skuId = createRandomId();

    const specPart = `0:${specIndex}`;
    const specNamePart = `${specPart}:${specName}:${specValue}`;

    const properties = variantName
      ? `${specPart};1:${variantIndex}`
      : specPart;
    const propertiesName = variantName
      ? `${specNamePart};1:${variantIndex}:${variantName}:${variantValue}`
      : specNamePart;

    const fallbackImageUrl = imageState.files
      .map((fileEntry) => fileEntry.uploadedUrl)
      .find(Boolean) || '';

    return {
      skuId,
      payload: {
        properties,
        properties_name: propertiesName,
        url: imageUrl || fallbackImageUrl,
        price: Number.isFinite(parsedPrice) ? parsedPrice : 0,
        total_price: Number.isFinite(parsedPrice) && Number.isFinite(parsedQty) ? parsedPrice * parsedQty : 0,
        qty: Number.isFinite(parsedQty) ? parsedQty : 0,
      },
    };
  };

  const validateSpecFields = (spec, includePricing = true) => {
    if (!spec.name?.trim() || !spec.value?.trim()) {
      return t('manualItemAdd:validation.specNameAndValueRequired');
    }
    if (includePricing) {
      if (!spec.price) {
        return t('manualItemAdd:validation.specPriceRequired');
      }
      if (!spec.quantity) {
        return t('manualItemAdd:validation.specQuantityRequired');
      }
      if (Number(spec.price) <= 0) {
        return t('manualItemAdd:validation.specPriceMustBeGreaterThanZero');
      }
      if (Number(spec.quantity) <= 0) {
        return t('manualItemAdd:validation.specQuantityMustBeGreaterThanZero');
      }
    }
    return '';
  };

  const validateVariantFields = (variant) => {
    if (!variant.name?.trim() || !variant.value?.trim()) {
      return t('manualItemAdd:validation.variantNameAndValueRequired');
    }
    if (!variant.price) {
      return t('manualItemAdd:validation.variantPriceRequired');
    }
    if (!variant.quantity) {
      return t('manualItemAdd:validation.variantQuantityRequired');
    }
    if (Number(variant.price) <= 0) {
      return t('manualItemAdd:validation.variantPriceMustBeGreaterThanZero');
    }
    if (Number(variant.quantity) <= 0) {
      return t('manualItemAdd:validation.variantQuantityMustBeGreaterThanZero');
    }
    return '';
  };

  const handleGenerateSpecSku = (specId) => {
    const specIndex = specs.findIndex((spec) => spec.id === specId);
    if (specIndex === -1) return;

    const spec = specs[specIndex];
    const errorMessage = validateSpecFields(spec, true);
    if (errorMessage) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: errorMessage,
      });
      return;
    }

    const { skuId, payload } = buildSkuPayload({
      specIndex,
      specName: spec.name.trim(),
      specValue: spec.value.trim(),
      imageUrl: spec.imageUrl?.trim() || '',
      price: spec.price,
      quantity: spec.quantity,
    });

    setSkuMap((prev) => ({
      ...prev,
      [skuId]: payload,
    }));

    setSpecs((prev) =>
      prev.map((entry) =>
        entry.id === specId
          ? {
              ...entry,
              confirmed: true,
              skuId,
            }
          : entry
      )
    );

    setSnackbar({
      open: true,
      severity: 'success',
      message: t('manualItemAdd:messages.specConvertedToSkuSuccess'),
    });
  };

  const handleGenerateVariantSku = (specId, variantId) => {
    const specIndex = specs.findIndex((spec) => spec.id === specId);
    if (specIndex === -1) return;

    const spec = specs[specIndex];
    const specValidation = validateSpecFields(spec, false);
    if (specValidation) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: specValidation,
      });
      return;
    }

    const variantIndex = spec.variants.findIndex((variant) => variant.id === variantId);
    if (variantIndex === -1) return;

    const variant = spec.variants[variantIndex];
    const variantValidation = validateVariantFields(variant);
    if (variantValidation) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: variantValidation,
      });
      return;
    }

    const { skuId, payload } = buildSkuPayload({
      specIndex,
      variantIndex,
      specName: spec.name.trim(),
      specValue: spec.value.trim(),
      variantName: variant.name.trim(),
      variantValue: variant.value.trim(),
      imageUrl: variant.imageUrl?.trim() || spec.imageUrl?.trim() || '',
      price: variant.price,
      quantity: variant.quantity,
    });

    setSkuMap((prev) => ({
      ...prev,
      [skuId]: payload,
    }));

    setSpecs((prev) =>
      prev.map((entry) => {
        if (entry.id !== specId) {
          return entry;
        }
        return {
          ...entry,
          variants: entry.variants.map((item) =>
            item.id === variantId
              ? {
                  ...item,
                  confirmed: true,
                  skuId,
                }
              : item
          ),
        };
      })
    );

    setSnackbar({
      open: true,
      severity: 'success',
      message: t('manualItemAdd:messages.variantConvertedToSkuSuccess'),
    });
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const manualItemPayload = useMemo(() => {
    if (!Object.keys(skuMap).length) {
      return null;
    }
    const parsedPrice = Number(formState.price);
    const parsedPostFee = Number(formState.postFee);
    const profitPercentage = parseBoundedProfitPercentage(formState.profitPercentage);
    const basePriceValue = Number.isFinite(parsedPrice) ? parsedPrice : 0;
    const priceValue = applyProfitPercentage(basePriceValue, profitPercentage);
    const postFeeValue = Number.isFinite(parsedPostFee) ? parsedPostFee : 0;
    const pricedSkuMap = Object.entries(skuMap).reduce((acc, [skuId, data]) => {
      const baseSkuPrice = Number(data.price);
      const skuPriceValue = Number.isFinite(baseSkuPrice) ? baseSkuPrice : 0;
      const qtyValue = Number(data.qty);
      const quantity = Number.isFinite(qtyValue) ? qtyValue : 0;
      const markedUpPrice = applyProfitPercentage(skuPriceValue, profitPercentage);

      acc[skuId] = {
        ...data,
        manual_price_base_price: skuPriceValue,
        manual_price_profit_percentage: profitPercentage,
        price: markedUpPrice,
        total_price: Number((markedUpPrice * quantity).toFixed(2)),
      };
      return acc;
    }, {});
    const uploadedImageUrls = imageState.files
      .map((fileEntry) => fileEntry.uploadedUrl)
      .filter(Boolean);
    const pictureUrl = uploadedImageUrls[0] || '';

    return {
      num_iid: createRandomId(),
      title: formState.title || '',
      price: priceValue,
      original_price: priceValue,
      orginal_price: priceValue,
      manual_price_base_price: basePriceValue,
      manual_price_profit_percentage: profitPercentage,
      nick: formState.userIdentifier || '_',
      min_num: 1,
      detail_url: formState.detailUrl.trim() || 'https://manual-entry.checkstall/item',
      pic_url: pictureUrl,
      post_fee: postFeeValue,
      seller_id: createRandomId(),
      priceRange: [],
      seller_info: {
        nick: formState.userIdentifier || '_',
        title: formState.title || '',
        zhuy: formState.title || '',
        shop_name: formState.title || '',
      },
      discount_price: 0,
      sku: pricedSkuMap,
      images: uploadedImageUrls,
    };
  }, [formState, imageState.files, skuMap]);

  const validateForm = () => {
    const nextErrors = {};
    if (!formState.title.trim()) {
      nextErrors.title = t('manualItemAdd:form.titleRequired');
    }
    if (!formState.userIdentifier.trim()) {
      nextErrors.userIdentifier = t('manualItemAdd:form.userIdentifierRequired');
    }
    if (formState.isProductRequestItem && !formState.productRequestNumber.trim()) {
      nextErrors.productRequestNumber = t('manualItemAdd:form.productRequestNumberRequired');
    }
    if (formState.isProductRequestItem && !formState.productRequestItemId.trim()) {
      nextErrors.productRequestItemId = t('manualItemAdd:form.productRequestItemIdRequired');
    }
    if (!formState.price) {
      nextErrors.price = t('manualItemAdd:form.priceRequired');
    } else if (Number(formState.price) <= 0) {
      nextErrors.price = t('manualItemAdd:form.priceMustBeGreaterThanZero');
    }
    const parsedProfitPercentage = Number(formState.profitPercentage);
    if (formState.profitPercentage === '') {
      nextErrors.profitPercentage = t('manualItemAdd:form.profitPercentageRequired');
    } else if (
      !Number.isFinite(parsedProfitPercentage)
      || parsedProfitPercentage < minProfitPercentage
      || parsedProfitPercentage > maxProfitPercentage
    ) {
      nextErrors.profitPercentage = t('manualItemAdd:form.profitPercentageOutOfRange', {
        min: minProfitPercentage,
        max: maxProfitPercentage,
      });
    }
    if (!formState.postFee) {
      nextErrors.postFee = t('manualItemAdd:form.postFeeRequired');
    } else if (Number(formState.postFee) < 0) {
      nextErrors.postFee = t('manualItemAdd:form.postFeeCannotBeNegative');
    }
    if (imageState.uploading) {
      nextErrors.image = t('manualItemAdd:form.imageUploadInProgress');
    } else if (!imageState.files.some((fileEntry) => fileEntry.uploadedUrl)) {
      nextErrors.image = t('manualItemAdd:form.pleaseUploadAtLeastOneImage');
    }
    if (!Object.keys(skuMap).length) {
      nextErrors.sku = t('manualItemAdd:validation.pleaseConvertAtLeastOneSku');
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (imageState.uploading) {
      setSnackbar({
        open: true,
        severity: 'info',
        message: t('manualItemAdd:messages.pleaseWaitForImageUpload'),
      });
      return;
    }

    if (!validateForm()) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: t('manualItemAdd:validation.pleaseResolveIssues'),
      });
      return;
    }

    if (!manualItemPayload) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: t('manualItemAdd:validation.generateAtLeastOneSku'),
      });
      return;
    }

    setSubmitting(true);
    setManualResponse(null);

    try {
      const payloadToSend = JSON.parse(JSON.stringify(manualItemPayload));
      payloadToSend.is_manual = true;

      const response = await addManualCartItem({
        cartItem: payloadToSend,
        userIdentifier: formState.userIdentifier.trim(),
        isProductRequestItem: formState.isProductRequestItem,
        productRequestNumber: formState.productRequestNumber.trim(),
        productRequestItemId: formState.productRequestItemId.trim(),
      });

      setPreviewPayload(payloadToSend);
      setManualResponse(response);
      setSnackbar({
        open: true,
        severity: 'success',
        message: response?.message || t('manualItemAdd:messages.manualCartItemAddedSuccess'),
      });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        t('manualItemAdd:messages.failedToAddManualCartItem');

      setSnackbar({
        open: true,
        severity: 'error',
        message,
      });

      if (error?.response?.data?.errors) {
        setErrors((prev) => ({
          ...prev,
          ...error.response.data.errors,
        }));
      }
      if (error?.response?.status === 404) {
        setErrors((prev) => ({
          ...prev,
          userIdentifier: message,
        }));
      }
      if (formState.isProductRequestItem && error?.response?.status === 400) {
        setErrors((prev) => ({
          ...prev,
          productRequestNumber: error?.response?.data?.message || prev.productRequestNumber,
          productRequestItemId: error?.response?.data?.message || prev.productRequestItemId,
        }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const skuRows = useMemo(
    () =>
      Object.entries(manualItemPayload?.sku || {}).map(([skuId, data]) => ({
        skuId,
        properties: data.properties_name,
        qty: data.qty,
        price: data.price,
        total: data.total_price,
      })),
    [manualItemPayload]
  );

  const skuTotals = useMemo(
    () =>
      skuRows.reduce(
        (acc, row) => ({
          qty: acc.qty + (Number(row.qty) || 0),
          total: acc.total + (Number(row.total) || 0),
        }),
        { qty: 0, total: 0 }
      ),
    [skuRows]
  );

  const exampleSpecs = useMemo(
    () => [
      {
        title: 'Specification #1',
        fields: {
          name: 'color',
          value: 'white',
          price: '4500',
          quantity: '4',
        },
        variants: [
          {
            id: '815365',
            name: 'size',
            value: '38',
            price: '4500',
            quantity: '2',
          },
          {
            id: '573637',
            name: 'size',
            value: '39',
            price: '4500',
            quantity: '2',
          },
        ],
      },
      {
        title: 'Specification #2',
        fields: {
          name: 'color',
          value: 'red',
          price: '4500',
          quantity: '6',
        },
        variants: [
          {
            id: '240013',
            name: 'size',
            value: '38',
            price: '4800',
            quantity: '3',
          },
          {
            id: '905165',
            name: 'size',
            value: '39',
            price: '4600',
            quantity: '3',
          },
        ],
      },
    ],
    []
  );

  const renderSpecificationExample = (spec, index) => (
    <Card key={spec.title} variant="outlined" sx={{ mb: 4 }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {t('manualItemAdd:helpModal.exampleSpec', { number: index + 1 })}
        </Typography>
        <Grid container spacing={1.5} mb={2}>
          {Object.entries(spec.fields).map(([field, value]) => (
            <Grid key={field} size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label={field.charAt(0).toUpperCase() + field.slice(1)}
                value={value}
                size="small"
                fullWidth
                InputProps={{ readOnly: true }}
              />
            </Grid>
          ))}
        </Grid>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3}>
          <Button variant="contained" color="error" startIcon={<AddIcon />} disabled>
            {t('manualItemAdd:helpModal.exampleAddVariant')}
          </Button>
          <Tooltip
            title={
              spec.variants.length
                ? t('manualItemAdd:helpModal.exampleDisabledWithVariants')
                : t('manualItemAdd:helpModal.exampleCreateSkuFromSpec')
            }
          >
            <span>
              <Button
                variant="outlined"
                color="success"
                startIcon={<CheckCircle />}
                disabled={Boolean(spec.variants.length)}
              >
                {t('manualItemAdd:helpModal.exampleGenerateSkuFromSpec')}
              </Button>
            </span>
          </Tooltip>
        </Stack>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          {t('manualItemAdd:helpModal.exampleVariants')}
        </Typography>
        <Stack spacing={2}>
          {spec.variants.map((variant, index) => (
            <Card
              key={variant.id}
              variant="outlined"
              sx={{
                borderColor: theme.palette.success.light,
                backgroundColor: theme.palette.success.light + '10',
              }}
            >
              <CardContent>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  justifyContent="space-between"
                  mb={2}
                >
                  <Typography variant="subtitle2" fontWeight="bold">
                    {t('manualItemAdd:helpModal.exampleVariantNumber', { number: index + 1 })}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip size="small" icon={<CheckCircle fontSize="small" />} label={t('manualItemAdd:helpModal.exampleSkuReady')} color="success" />
                    <Chip size="small" label={t('manualItemAdd:specifications.skuId', { id: variant.id })} variant="outlined" />
                  </Stack>
                </Stack>
                <Grid container spacing={1.5}>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField label={t('manualItemAdd:specifications.variantName')} value={variant.name} size="small" fullWidth InputProps={{ readOnly: true }} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField label={t('manualItemAdd:specifications.variantValue')} value={variant.value} size="small" fullWidth InputProps={{ readOnly: true }} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField label={t('manualItemAdd:specifications.price')} value={variant.price} size="small" fullWidth InputProps={{ readOnly: true }} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField label={t('manualItemAdd:specifications.quantity')} value={variant.quantity} size="small" fullWidth InputProps={{ readOnly: true }} />
                  </Grid>
                </Grid>
                <Box mt={2}>
                  <Button variant="contained" color="success" startIcon={<CheckCircle />} disabled>
                    {t('manualItemAdd:helpModal.exampleGenerateSkuFromVariant')}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        px: { xs: 0.75, sm: 1, md: 1.5 },
        py: { xs: 2, sm: 2.5, md: 3 },
        maxWidth: '95vw',
        mx: 'auto',
        overflowX: 'hidden',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {t('manualItemAdd:title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('manualItemAdd:description')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="text"
            startIcon={<LiveHelp />}
            onClick={() => setHelpModalOpen(true)}
          >
            {t('manualItemAdd:howItWorks')}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<RestartAlt />}
            onClick={resetForm}
          >
            {t('manualItemAdd:reset')}
          </Button>
          <Button
            variant="contained"
            startIcon={<CheckCircle />}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? t('manualItemAdd:adding') : t('manualItemAdd:addToCart')}
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 7 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                {t('manualItemAdd:sections.itemDetails')}
              </Typography>
                <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label={t('manualItemAdd:form.title')}
                    fullWidth
                    value={formState.title}
                    onChange={handleInputChange('title')}
                    error={Boolean(errors.title)}
                    helperText={errors.title}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label={t('manualItemAdd:form.description')}
                    fullWidth
                    multiline
                    minRows={3}
                    value={formState.description}
                    onChange={handleInputChange('description')}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label={t('manualItemAdd:form.userEmailPhone')}
                    fullWidth
                    value={formState.userIdentifier}
                    onChange={handleInputChange('userIdentifier')}
                    error={Boolean(errors.userIdentifier)}
                    helperText={errors.userIdentifier}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={formState.isProductRequestItem}
                        onChange={handleInputChange('isProductRequestItem')}
                      />
                    )}
                    label={t('manualItemAdd:form.isProductRequestItem')}
                  />
                </Grid>
                {formState.isProductRequestItem && (
                  <>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label={t('manualItemAdd:form.productRequestNumber')}
                        fullWidth
                        value={formState.productRequestNumber}
                        onChange={handleInputChange('productRequestNumber')}
                        error={Boolean(errors.productRequestNumber)}
                        helperText={errors.productRequestNumber || t('manualItemAdd:form.productRequestNumberHelp')}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label={t('manualItemAdd:form.productRequestItemId')}
                        fullWidth
                        value={formState.productRequestItemId}
                        onChange={handleInputChange('productRequestItemId')}
                        error={Boolean(errors.productRequestItemId)}
                        helperText={errors.productRequestItemId || t('manualItemAdd:form.productRequestItemIdHelp')}
                      />
                    </Grid>
                  </>
                )}
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label={t('manualItemAdd:form.detailUrl')}
                    fullWidth
                    value={formState.detailUrl}
                    onChange={handleInputChange('detailUrl')}
                    helperText={t('manualItemAdd:form.detailUrlHelp')}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label={t('manualItemAdd:form.unitPrice')}
                    fullWidth
                    value={formState.price}
                    onChange={handleInputChange('price')}
                    error={Boolean(errors.price)}
                    helperText={
                      errors.price
                      || t('manualItemAdd:form.unitPriceHelp', {
                        price: applyProfitPercentage(
                          formState.price,
                          parseBoundedProfitPercentage(formState.profitPercentage)
                        ).toLocaleString(),
                      })
                    }
                    InputProps={{
                      startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                      inputMode: 'decimal',
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label={t('manualItemAdd:form.profitPercentage')}
                    fullWidth
                    value={formState.profitPercentage}
                    onChange={handleInputChange('profitPercentage')}
                    error={Boolean(errors.profitPercentage)}
                    helperText={
                      errors.profitPercentage
                      || t('manualItemAdd:form.profitPercentageHelp', {
                        defaultValue: defaultProfitPercentage,
                        min: minProfitPercentage,
                        max: maxProfitPercentage,
                      })
                    }
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      inputMode: 'decimal',
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label={t('manualItemAdd:form.postFee')}
                    fullWidth
                    value={formState.postFee}
                    onChange={handleInputChange('postFee')}
                    error={Boolean(errors.postFee)}
                    helperText={errors.postFee}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                      inputMode: 'decimal',
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
            <Divider />
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                {t('manualItemAdd:sections.itemImage')}
              </Typography>
              <Paper
                elevation={imageState.dragActive ? 8 : 2}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  border: imageState.dragActive ? '2px dashed #4CAF50' : '2px dashed #ccc',
                  backgroundColor: imageState.dragActive ? '#E8F5E8' : 'transparent',
                  transition: 'all 0.3s ease',
                  mb: 2,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={handleUploadClick}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  multiple
                  accept={acceptedFileTypes.join(',')}
                  onChange={handleImageChange}
                />
                <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {imageState.uploading ? t('manualItemAdd:form.uploading') : t('manualItemAdd:form.uploadImages')}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('manualItemAdd:form.fileTypes', { maxSize: Math.round(maxFileSize / (1024 * 1024)) })}
                </Typography>
                <Typography variant="body2" color="primary" sx={{ mt: 1, fontWeight: 'bold' }}>
                  {t('manualItemAdd:form.imagesHelp')}
                </Typography>
              </Paper>
              {imageState.uploading && (
                <Box sx={{ width: '100%', maxWidth: 320, mt: 2 }}>
                  <LinearProgress />
                </Box>
              )}
              {!!imageState.files.length && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {imageState.files.map((fileEntry, index) => (
                    <Grid key={fileEntry.id} size={{ xs: 12, sm: 6, md: 4 }}>
                      <Card variant="outlined" sx={{ position: 'relative', height: '100%' }}>
                        <CardContent sx={{ pb: '16px !important' }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                            <Chip
                              size="small"
                              color={index === 0 ? 'primary' : 'default'}
                              label={index === 0 ? t('manualItemAdd:form.mainImage') : t('manualItemAdd:form.additionalImage', { number: index + 1 })}
                            />
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleRemoveImage(fileEntry.id);
                              }}
                              disabled={fileEntry.deleting}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                          <Box
                            component="img"
                            src={fileEntry.preview}
                            alt={fileEntry.name}
                            sx={{
                              width: '100%',
                              height: 160,
                              objectFit: 'contain',
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                              backgroundColor: 'background.default',
                              mb: 1.5,
                            }}
                          />
                          <Typography variant="body2" fontWeight={600} noWrap title={fileEntry.name}>
                            {fileEntry.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {formatFileSize(fileEntry.size)}
                          </Typography>
                          {fileEntry.uploadedUrl ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ wordBreak: 'break-all', display: 'block', mt: 1 }}
                            >
                              {fileEntry.uploadedUrl}
                            </Typography>
                          ) : (
                            <Chip size="small" color="warning" label={t('manualItemAdd:form.uploading')} sx={{ mt: 1 }} />
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
              {!imageState.files.length && (
                <Typography variant="body2" color="text.secondary">
                  {t('manualItemAdd:form.noImagesSelected')}
                </Typography>
              )}
              {errors.image && (
                <Typography variant="caption" color="error" display="block" mt={1}>
                  {errors.image}
                </Typography>
              )}
            </CardContent>
          </Card>

          <Box mt={3}>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" fontWeight="bold">
                    {t('manualItemAdd:sections.specificationsVariants')}
                  </Typography>
                  <Button startIcon={<AddCircleOutline />} variant="contained" onClick={handleAddSpec}>
                    {t('manualItemAdd:specifications.addSpecification')}
                  </Button>
                </Stack>
                {errors.sku && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {errors.sku}
                  </Alert>
                )}
                {!specs.length && (
                  <Typography variant="body2" color="text.secondary">
                    {t('manualItemAdd:specifications.startByAdding')}
                  </Typography>
                )}
                <Stack spacing={2}>
                  {specs.map((spec, specIdx) => (
                    <Card key={spec.id} variant="outlined" sx={{ borderColor: spec.confirmed ? 'success.light' : 'divider' }}>
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {t('manualItemAdd:specifications.specification', { number: specIdx + 1 })}
                          </Typography>
                          {spec.confirmed && (
                            <Chip
                              size="small"
                              icon={<CheckCircle fontSize="small" />}
                              label={t('manualItemAdd:specifications.skuReady')}
                              color="success"
                              variant="outlined"
                            />
                          )}
                          {!!spec.skuId && (
                            <Chip
                              size="small"
                              label={t('manualItemAdd:specifications.skuId', { id: spec.skuId })}
                              variant="outlined"
                              sx={{ ml: 1 }}
                            />
                          )}
                          <Box flexGrow={1} />
                          <Tooltip title={t('manualItemAdd:specifications.removeSpecification')}>
                            <IconButton color="error" onClick={() => handleRemoveSpec(spec.id)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField
                              label={t('manualItemAdd:specifications.name')}
                              fullWidth
                              value={spec.name}
                              onChange={(event) => updateSpecField(spec.id, 'name', event.target.value)}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField
                              label={t('manualItemAdd:specifications.value')}
                              fullWidth
                              value={spec.value}
                              onChange={(event) => updateSpecField(spec.id, 'value', event.target.value)}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField
                              label={t('manualItemAdd:specifications.price')}
                              fullWidth
                              value={spec.price}
                              onChange={(event) => updateSpecField(spec.id, 'price', event.target.value)}
                              helperText={t('manualItemAdd:specifications.customerPriceHelp', {
                                price: applyProfitPercentage(
                                  spec.price,
                                  parseBoundedProfitPercentage(formState.profitPercentage)
                                ).toLocaleString(),
                              })}
                              InputProps={{
                                inputMode: 'decimal',
                              }}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField
                              label={t('manualItemAdd:specifications.quantity')}
                              fullWidth
                              value={spec.quantity}
                              onChange={(event) => updateSpecField(spec.id, 'quantity', event.target.value)}
                              InputProps={{
                                inputMode: 'numeric',
                              }}
                            />
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              label={t('manualItemAdd:specifications.imageUrl')}
                              fullWidth
                              value={spec.imageUrl}
                              onChange={(event) => updateSpecField(spec.id, 'imageUrl', event.target.value)}
                              helperText={t('manualItemAdd:specifications.imageUrlHelp')}
                            />
                          </Grid>
                        </Grid>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={2}>
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={() => handleAddVariant(spec.id)}
                          >
                            {t('manualItemAdd:specifications.addVariant')}
                          </Button>
                          <Tooltip title={t('manualItemAdd:specifications.useWhenNoVariants')}>
                            <Button
                              variant="outlined"
                              color="success"
                              startIcon={<CheckCircle />}
                              onClick={() => handleGenerateSpecSku(spec.id)}
                              disabled={spec.variants && spec.variants.length > 0}
                            >
                              {t('manualItemAdd:specifications.generateSkuFromSpec')}
                            </Button>
                          </Tooltip>
                        </Stack>
                      </CardContent>
                      {!!spec.variants.length && (
                        <>
                          <Divider />
                          <CardContent>
                            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {t('manualItemAdd:specifications.variants')}
                              </Typography>
                              <Tooltip title={t('manualItemAdd:specifications.eachVariantProducesSku')}>
                                <InfoOutlined fontSize="small" color="action" />
                              </Tooltip>
                            </Stack>
                            <Stack spacing={2}>
                              {spec.variants.map((variant, variantIdx) => (
                                <Card
                                  key={variant.id}
                                  variant="outlined"
                                  sx={{ borderColor: variant.confirmed ? 'success.light' : 'divider' }}
                                >
                                  <CardContent>
                                    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                                      <Typography variant="subtitle2" fontWeight="bold">
                                        {t('manualItemAdd:specifications.variant', { number: variantIdx + 1 })}
                                      </Typography>
                                      {variant.confirmed && (
                                        <Chip
                                          size="small"
                                          icon={<CheckCircle fontSize="small" />}
                                          label={t('manualItemAdd:specifications.skuReady')}
                                          color="success"
                                          variant="outlined"
                                        />
                                      )}
                                      {!!variant.skuId && (
                                        <Chip
                                          size="small"
                                          label={t('manualItemAdd:specifications.skuId', { id: variant.skuId })}
                                          variant="outlined"
                                          sx={{ ml: 1 }}
                                        />
                                      )}
                                      <Box flexGrow={1} />
                                      <Tooltip title={t('manualItemAdd:specifications.removeVariant')}>
                                        <IconButton
                                          color="error"
                                          onClick={() => handleRemoveVariant(spec.id, variant.id)}
                                        >
                                          <CancelIcon />
                                        </IconButton>
                                      </Tooltip>
                                    </Stack>
                                    <Grid container spacing={2}>
                                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                        <TextField
                                          label={t('manualItemAdd:specifications.variantName')}
                                          fullWidth
                                          value={variant.name}
                                          onChange={(event) =>
                                            updateVariantField(spec.id, variant.id, 'name', event.target.value)
                                          }
                                        />
                                      </Grid>
                                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                        <TextField
                                          label={t('manualItemAdd:specifications.variantValue')}
                                          fullWidth
                                          value={variant.value}
                                          onChange={(event) =>
                                            updateVariantField(spec.id, variant.id, 'value', event.target.value)
                                          }
                                        />
                                      </Grid>
                                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                        <TextField
                                          label={t('manualItemAdd:specifications.price')}
                                          fullWidth
                                          value={variant.price}
                                          onChange={(event) =>
                                            updateVariantField(spec.id, variant.id, 'price', event.target.value)
                                          }
                                          helperText={t('manualItemAdd:specifications.customerPriceHelp', {
                                            price: applyProfitPercentage(
                                              variant.price,
                                              parseBoundedProfitPercentage(formState.profitPercentage)
                                            ).toLocaleString(),
                                          })}
                                          InputProps={{
                                            inputMode: 'decimal',
                                          }}
                                        />
                                      </Grid>
                                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                        <TextField
                                          label={t('manualItemAdd:specifications.quantity')}
                                          fullWidth
                                          value={variant.quantity}
                                          onChange={(event) =>
                                            updateVariantField(spec.id, variant.id, 'quantity', event.target.value)
                                          }
                                          InputProps={{
                                            inputMode: 'numeric',
                                          }}
                                        />
                                      </Grid>
                                      <Grid size={{ xs: 12 }}>
                                        <TextField
                                          label={t('manualItemAdd:specifications.variantImageUrl')}
                                          fullWidth
                                          value={variant.imageUrl}
                                          onChange={(event) =>
                                            updateVariantField(spec.id, variant.id, 'imageUrl', event.target.value)
                                          }
                                          helperText={t('manualItemAdd:specifications.imageUrlHelp')}
                                        />
                                      </Grid>
                                    </Grid>
                                  </CardContent>
                                  <CardActions sx={{ px: 2, pb: 2 }}>
                                    <Button
                                      variant="outlined"
                                      color="success"
                                      startIcon={<CheckCircle />}
                                      onClick={() => handleGenerateVariantSku(spec.id, variant.id)}
                                    >
                                      {t('manualItemAdd:specifications.generateSkuFromVariant')}
                                    </Button>
                                  </CardActions>
                                </Card>
                              ))}
                            </Stack>
                          </CardContent>
                        </>
                      )}
                    </Card>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, lg: 5 }}>
          <Stack spacing={3}>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <Typography variant="h6" fontWeight="bold">
                    {t('manualItemAdd:sections.generatedSkus', { count: skuRows.length })}
                  </Typography>
                  <Tooltip title={t('manualItemAdd:table.reflectsCombinations')}>
                    <InfoOutlined fontSize="small" color="action" />
                  </Tooltip>
                </Stack>
                {!skuRows.length ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('manualItemAdd:table.noSkusGenerated')}
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('manualItemAdd:table.skuId')}</TableCell>
                          <TableCell>{t('manualItemAdd:table.properties')}</TableCell>
                          <TableCell align="right">{t('manualItemAdd:table.qty')}</TableCell>
                          <TableCell align="right">{t('manualItemAdd:table.price')}</TableCell>
                          <TableCell align="right">{t('manualItemAdd:table.total')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {skuRows.map((row) => (
                          <TableRow key={row.skuId}>
                            <TableCell>{row.skuId}</TableCell>
                            <TableCell>
                              <Typography variant="body2">{row.properties}</Typography>
                            </TableCell>
                            <TableCell align="right">{row.qty}</TableCell>
                            <TableCell align="right">¥{row.price?.toLocaleString()}</TableCell>
                            <TableCell align="right">¥{row.total?.toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                        {!!skuRows.length && (
                          <TableRow>
                            <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>
                              {t('manualItemAdd:table.summary')}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                              {skuTotals.qty.toLocaleString()}
                            </TableCell>
                            <TableCell align="right">-</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                              ¥{skuTotals.total.toLocaleString()}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <Typography variant="h6" fontWeight="bold">
                    {t('manualItemAdd:payloadPreview.title')}
                  </Typography>
                  <Tooltip title={t('manualItemAdd:payloadPreview.description')}>
                    <InfoOutlined fontSize="small" color="action" />
                  </Tooltip>
                </Stack>
                {previewPayload ? (
                  <Box
                    component="pre"
                    sx={{
                      maxHeight: 360,
                      overflow: 'auto',
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'grey.900',
                      color: 'grey.100',
                      fontSize: 12,
                    }}
                  >
                    {JSON.stringify(previewPayload, null, 2)}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('manualItemAdd:payloadPreview.generateDraftToInspect')}
                  </Typography>
                )}
              </CardContent>
            </Card>
            {manualResponse && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {t('manualItemAdd:sections.serverResponse')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {manualResponse?.message || t('manualItemAdd:messages.manualCartItemAddedSuccess')}
                  </Typography>
                  <Table size="small">
                    <TableBody>
                      {manualResponse.cart_id && (
                        <TableRow>
                          <TableCell>{t('manualItemAdd:serverResponse.cartId')}</TableCell>
                          <TableCell>{manualResponse.cart_id}</TableCell>
                        </TableRow>
                      )}
                      {manualResponse.user && (
                        <>
                          <TableRow>
                            <TableCell>{t('manualItemAdd:serverResponse.user')}</TableCell>
                            <TableCell>{manualResponse.user.username}</TableCell>
                          </TableRow>
                          {manualResponse.user.email && (
                            <TableRow>
                              <TableCell>{t('manualItemAdd:serverResponse.email')}</TableCell>
                              <TableCell>{manualResponse.user.email}</TableCell>
                            </TableRow>
                          )}
                          {manualResponse.user.phone && (
                            <TableRow>
                              <TableCell>{t('manualItemAdd:serverResponse.phone')}</TableCell>
                              <TableCell>{manualResponse.user.phone}</TableCell>
                            </TableRow>
                          )}
                          {manualResponse.product_request && (
                            <>
                              <TableRow>
                                <TableCell>{t('manualItemAdd:serverResponse.productRequestNumber')}</TableCell>
                                <TableCell>{manualResponse.product_request.request_number}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>{t('manualItemAdd:serverResponse.productRequestStatus')}</TableCell>
                                <TableCell>{manualResponse.product_request.status}</TableCell>
                              </TableRow>
                              {manualResponse.product_request_item && (
                                <>
                                  <TableRow>
                                    <TableCell>{t('manualItemAdd:serverResponse.productRequestItemId')}</TableCell>
                                    <TableCell>{manualResponse.product_request_item.id}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell>{t('manualItemAdd:serverResponse.productRequestItemStatus')}</TableCell>
                                    <TableCell>{manualResponse.product_request_item.status}</TableCell>
                                  </TableRow>
                                </>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </Stack>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Dialog
        open={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
        fullWidth
        maxWidth="md"
        fullScreen={isSmallScreen}
      >
        <DialogTitle>{t('manualItemAdd:helpModal.title')}</DialogTitle>
        <DialogContent dividers>
          <DialogContentText component="div" sx={{ mb: 2 }}>
            <Typography variant="body1" paragraph>
              <span dangerouslySetInnerHTML={{ __html: t('manualItemAdd:helpModal.intro') }} />
            </Typography>
            <Typography variant="body1" paragraph>
              {t('manualItemAdd:helpModal.workflowTitle')}
            </Typography>
            <ol style={{ paddingLeft: theme.spacing(3), marginTop: 0, marginBottom: theme.spacing(2) }}>
              <li>
                {t('manualItemAdd:helpModal.step1')}
              </li>
              <li>
                <span dangerouslySetInnerHTML={{ __html: t('manualItemAdd:helpModal.step2') }} />
              </li>
              <li>
                <span dangerouslySetInnerHTML={{ __html: t('manualItemAdd:helpModal.step3') }} />
              </li>
              <li>
                {t('manualItemAdd:helpModal.step4')}
              </li>
            </ol>
            <Typography variant="body1" paragraph>
              {t('manualItemAdd:helpModal.examplesTitle')}
            </Typography>
          </DialogContentText>
          <Divider sx={{ mb: 3 }} />
          {exampleSpecs.map((spec, index) => renderSpecificationExample(spec, index))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpModalOpen(false)} autoFocus>
            {t('manualItemAdd:helpModal.gotIt')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  </Box>
  );
};

export default ManualItemAddPage;

